import { toPng } from "html-to-image";
import { useRef, useState, useEffect } from "react";
import { Download, CheckCircle, Clock } from "lucide-react";
import "./TicketCard.scss";

export default function TicketCard({ booking, tickets = [] }) {
  const ticketRefs = useRef([]);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [heroError, setHeroError] = useState(false);
  const [heroBase64, setHeroBase64] = useState(null);
  const [downloading, setDownloading] = useState(false);

  /* ================= PRELOAD HERO IMAGE ================= */
  useEffect(() => {
    const url = booking?.event?.headerImage?.url;
    if (!url) {
      setHeroError(true);
      return;
    }

    setHeroLoaded(false);
    setHeroError(false);
    setHeroBase64(null);

    // Standard browser load (for display)
    const img = new Image();
    img.src = url;
    img.onload = () => setHeroLoaded(true);
    img.onerror = () => setHeroError(true);

    // Fetch as base64 so html-to-image can use it in canvas without CORS issues
    fetch(url, { mode: "cors" })
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      )
      .then((dataUrl) => setHeroBase64(dataUrl))
      .catch(() => {
        // CORS fetch failed — download will use fallback gradient
        console.warn("Hero image CORS fetch failed; download will use fallback.");
      });
  }, [booking?.event?.headerImage?.url]);

  /* ================= DOWNLOAD SINGLE ================= */
  async function downloadTicket(index) {
    const ref = ticketRefs.current[index];
    if (!ref) return;

    try {
      // Brief settle for any final render
      await new Promise((resolve) => setTimeout(resolve, 200));

      const dataUrl = await toPng(ref, {
        pixelRatio: 3,
        backgroundColor: "#0B1220",
        cacheBurst: true,
        // Only allow data: URLs for images — external URLs taint canvas (SecurityError)
        // Skip external stylesheets (Google Fonts also taints canvas)
        filter: (node) => {
          // Exclude elements explicitly marked as screen-only
          if (node.getAttribute?.("data-canvas-skip") === "true") return false;
          if (node.tagName === "IMG") {
            const src = node.getAttribute?.("src") || node.src || "";
            return src.startsWith("data:");
          }
          if (node.tagName === "LINK" && node.rel === "stylesheet") {
            const href = node.href || "";
            if (href.startsWith("http") && !href.includes(window.location.hostname)) {
              return false;
            }
          }
          return true;
        },
      });

      const link = document.createElement("a");
      link.download = `ticket-${booking._id}-${index + 1}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Download failed:", err);
      alert("Gagal download tiket. Silakan coba lagi.");
    }
  }

  async function downloadAll() {
    setDownloading(true);
    for (let i = 0; i < tickets.length; i++) {
      await downloadTicket(i);
      if (i < tickets.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
    setDownloading(false);
  }

  // The src to use inside the captured ref — data: URL works in canvas
  const heroSrc = heroBase64 || booking?.event?.headerImage?.url;

  return (
    <div className="ticket-cards-container">
      {/* Header info - shared across all tickets */}
      <div className="ticket-header-info">
        <div className="ticket-event-title">{booking?.event?.title}</div>
        <div className="ticket-meta">
          <span>{booking?.customer?.name}</span>
          <span className="separator">•</span>
          <span>{booking?.ticket?.tierName}</span>
          <span className="separator">•</span>
          <span>{tickets.length} Tiket</span>
        </div>
      </div>

      {/* Individual ticket cards */}
      {tickets.map((ticket, index) => (
        <div key={ticket.code || index} className="ticket-card-item">
          {/* Ticket number badge */}
          <div className="ticket-number-badge">
            Tiket {index + 1} dari {tickets.length}
          </div>

          <div
            className="ticket-ultra"
            ref={(el) => (ticketRefs.current[index] = el)}
          >
            <div className="holo-layer" />
            <div className="light-sweep" />

            {/* HERO */}
            <div className="ticket-hero">
              {/* Always render fallback as canvas base layer (CSS-only, safe for canvas) */}
              <div className="hero-fallback" />

              {/* Canvas-compatible: only when base64 is ready (data: URL passes filter) */}
              {heroBase64 && !heroError && (
                <img src={heroBase64} alt="Event Banner" />
              )}

              {/* Screen-only: external URL for instant display before base64 loads.
                  Marked data-canvas-skip so it's excluded from toPng capture. */}
              {!heroBase64 && heroSrc && !heroError && (
                <img
                  src={heroSrc}
                  alt=""
                  data-canvas-skip="true"
                  onLoad={() => setHeroLoaded(true)}
                  onError={() => setHeroError(true)}
                  style={{
                    opacity: heroLoaded ? 1 : 0,
                    transition: "opacity .3s ease",
                  }}
                />
              )}

              <div className="hero-gradient" />

              <div className="hero-content">
                <span className="brand">RTC GKY</span>
                <h1>{booking?.event?.title}</h1>
              </div>
            </div>

            {/* BODY */}
            <div className="ticket-body">
              <div className="qr-zone">
                <div className="qr-pedestal">
                  {ticket.qr?.imageUrl ? (
                    <img
                      src={ticket.qr.imageUrl}
                      alt={`QR Ticket ${index + 1}`}
                    />
                  ) : (
                    <div className="qr-placeholder">Memuat...</div>
                  )}
                </div>
                <span className="scan-hint">Scan untuk akses masuk</span>
              </div>

              <div className="divider" />

              <div className="info-grid">
                <Info
                  label="Nama"
                  value={booking?.customer?.name}
                  variant="name"
                />
                <Info
                  label="Kode Tiket"
                  value={ticket.code || "-"}
                  mono
                />
                <Info
                  label="Tipe Tiket"
                  value={booking?.ticket?.tierName}
                />
                <Info
                  label="Event"
                  value={booking?.event?.title}
                />
              </div>

              <div className="ticket-status-row">
                {ticket.status === "used" ? (
                  <span className="status-badge used">
                    <CheckCircle size={12} /> Sudah digunakan
                  </span>
                ) : ticket.status === "active" ? (
                  <span className="status-badge active">
                    <Clock size={12} /> Aktif
                  </span>
                ) : null}
              </div>

              <div className="ticket-watermark">
                OFFICIAL ACCESS PASS
              </div>

              <div className="ticket-footer">
                Berlaku untuk 1x scan • Tidak dapat dipindahtangankan
              </div>
            </div>
          </div>

          <button
            className="download-single"
            onClick={() => downloadTicket(index)}
          >
            <Download size={16} />
            Download Tiket {index + 1}
          </button>
        </div>
      ))}

      {tickets.length > 1 && (
        <button
          className="download-ultra"
          onClick={downloadAll}
          disabled={downloading}
        >
          <Download size={18} />
          {downloading ? "Mendownload..." : `Download Semua (${tickets.length} Tiket)`}
        </button>
      )}
    </div>
  );
}

/* ================= INFO COMPONENT ================= */

function Info({ label, value, mono, variant }) {
  return (
    <div className={`info-item ${variant || ""}`}>
      <span className="label">{label}</span>
      <strong className={`value ${mono ? "mono" : ""}`}>
        {value || "-"}
      </strong>
    </div>
  );
}
